import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, doc, updateDoc } from 'firebase/firestore';
import { XMarkIcon, MagnifyingGlassIcon, ArrowTopRightOnSquareIcon, LinkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

function IdCardGallery() {
  const [idCards, setIdCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const getHighQualityImageUrl = (url) => {
    if (!url) return url;
    return url; // Return the original URL without any modifications
  };

  useEffect(() => {
    fetchIdCards();
  }, []);

  const fetchIdCards = async () => {
    try {
      setLoading(true);
      setError(null);
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);
      const cards = [];
      
      console.log('Total users found:', querySnapshot.docs.length);
      
      querySnapshot.docs.forEach(doc => {
        const userData = doc.data();
        console.log('User data:', {
          id: doc.id,
          email: userData.email,
          idCardUrl: userData.idCardUrl,
          fullName: userData.fullName
        });
        
        // Check if user has an ID card URL in their document
        if (userData.idCardUrl) {
          console.log('Found ID card for user:', userData.email);
          cards.push({
            id: doc.id,
            email: userData.email || '',
            fullName: userData.fullName || userData.name || 'Unnamed User',
            idCardUrl: getHighQualityImageUrl(userData.idCardUrl), // Use high quality URL
            createdAt: userData.registrationDate || userData.createdAt,
            status: userData.status || 'pending',
            country: userData.country || 'Not specified',
            mobileNumber: userData.mobileNumber || 'Not provided',
            accountLevel: userData.accountDetails?.accountLevel || 'standard'
          });
        }
      });
      
      console.log('Total ID cards found:', cards.length);
      
      if (cards.length === 0) {
        setError('No ID cards found in the database. Please check if the ID card URLs are being properly saved in the user documents.');
      }
      
      setIdCards(cards);
    } catch (error) {
      console.error('Error fetching ID cards:', error);
      setError('Failed to load ID cards. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCards = idCards.filter(card => {
    const matchesSearch = 
      card.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      card.country?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || card.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const handleDeleteIdCard = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this ID card? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(userId);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        idCardUrl: null,
        lastUpdated: new Date().toISOString()
      });
      
      // Update local state
      setIdCards(prevCards => prevCards.filter(card => card.id !== userId));
    } catch (error) {
      console.error('Error deleting ID card:', error);
      alert('Failed to delete ID card. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const ImageModal = () => {
    if (!showImageModal || !selectedImage) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b">
            <div>
              <h3 className="text-lg font-semibold">ID Card Details</h3>
              <p className="text-sm text-gray-500">{selectedImage.fullName}</p>
            </div>
            <div className="flex items-center space-x-4">
              <a
                href={selectedImage.idCardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 flex items-center"
              >
                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                <span className="ml-1">View Original</span>
              </a>
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setSelectedImage(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="p-4">
            <div className="relative aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={selectedImage.idCardUrl}
                alt="ID Card"
                className="w-full h-full object-contain"
                onError={(e) => {
                  console.error('Error loading image:', e);
                  e.target.src = 'https://via.placeholder.com/800x500?text=ID+Card+Not+Available';
                }}
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{selectedImage.email}</p>
              </div>
              <div>
                <p className="text-gray-500">Country</p>
                <p className="font-medium">{selectedImage.country}</p>
              </div>
              <div>
                <p className="text-gray-500">Registration Date</p>
                <p className="font-medium">
                  {selectedImage.createdAt ? new Date(selectedImage.createdAt).toLocaleDateString() : 'Not available'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedImage.status === 'verified' 
                      ? 'bg-green-100 text-green-800'
                      : selectedImage.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedImage.status.charAt(0).toUpperCase() + selectedImage.status.slice(1)}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <p className="text-gray-500">{error}</p>
        <button
          onClick={fetchIdCards}
          className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">ID Card Verification</h2>
        <div className="text-sm text-gray-500">
          Total ID Cards: {idCards.length}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name, email, or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
        <div className="w-full sm:w-48">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* ID Card Grid */}
      {filteredCards.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No ID cards found matching your search criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCards.map((card) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-4 bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-gray-900">{card.fullName}</h3>
                    <p className="text-sm text-gray-500">{card.email}</p>
                    <p className="text-xs text-gray-400 mt-1">{card.country}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDeleteIdCard(card.id)}
                      disabled={deletingId === card.id}
                      className={`text-red-600 hover:text-red-800 transition-colors ${
                        deletingId === card.id ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Delete ID Card"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      card.status === 'verified' 
                        ? 'bg-green-100 text-green-800'
                        : card.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {card.status.charAt(0).toUpperCase() + card.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div 
                className="relative aspect-[4/3] bg-gray-100 cursor-pointer"
                onClick={() => {
                  setSelectedImage(card);
                  setShowImageModal(true);
                }}
              >
                <img
                  src={card.idCardUrl}
                  alt={`ID Card for ${card.fullName}`}
                  className="w-full h-full object-contain hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x500?text=ID+Card+Not+Available';
                  }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ImageModal />
    </div>
  );
}

export default IdCardGallery; 